import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { ObjectBuilder, Image3D, Rectangle3D, RichTextGrammar } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { text } from "../../TextResources";
import { parseTextWithLinksToColoredRichText } from "../utils/LinkText";
import { StringUtils } from "../utils/StringUtils";
export class MessengerLayoutHelper {
    static get _nextControlId() { return ++MessengerLayoutHelper._currentControlId; }
    static createDateLine(params) {
        const ro = Env.common.messengerPanel.chat.messageContainer;
        const dl = ro.dateLine;
        const mesh2d = params.mesh2d;
        const id = MessengerLayoutHelper._nextControlId;
        const rootContainer = new Container(`dateLineContainer${id}`);
        rootContainer.heightInPixels = mesh2d.m2px(dl.height);
        const dateText = MessengerLayoutHelper._getDateText(params.date);
        const dateTextBlock = ObjectBuilder.createTextBlock3D(mesh2d, `dateLineDateText${id}`, dateText, dl.dateText);
        rootContainer.addControl(dateTextBlock);
        return {
            rootContainer,
            dateTextBlock,
            newMessagesTextBlock: null,
            statusImages: null,
        };
    }
    static createNewMessagesLine(params) {
        const ro = Env.common.messengerPanel.chat.messageContainer;
        const nm = ro.newMessagesLine;
        const mesh2d = params.mesh2d;
        const id = MessengerLayoutHelper._nextControlId;
        const rootContainer = new Container(`newMessagesLineContainer${id}`);
        rootContainer.heightInPixels = mesh2d.m2px(nm.height);
        const newMessagesTextBlock = ObjectBuilder.createTextBlock3D(mesh2d, `newMessagesLineText${id}`, text.common_messenger_new_messages_caption, nm.caption);
        rootContainer.addControl(newMessagesTextBlock);
        const leftLine = new Container(`newMessagesLeftLine${id}`);
        leftLine.widthInPixels = mesh2d.m2px(nm.line.width);
        leftLine.heightInPixels = mesh2d.m2px(nm.line.height);
        leftLine.background = nm.line.color;
        leftLine.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        leftLine.leftInPixels = mesh2d.m2px(nm.line.padding);
        rootContainer.addControl(leftLine);
        const rightLine = new Container(`newMessagesRightLine${id}`);
        rightLine.widthInPixels = mesh2d.m2px(nm.line.width);
        rightLine.heightInPixels = mesh2d.m2px(nm.line.height);
        rightLine.background = nm.line.color;
        rightLine.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_RIGHT;
        rightLine.leftInPixels = -mesh2d.m2px(nm.line.padding);
        rootContainer.addControl(rightLine);
        return {
            rootContainer,
            dateTextBlock: null,
            newMessagesTextBlock,
            statusImages: null,
        };
    }
    static createModelMessage(params) {
        const ro = Env.common.messengerPanel.chat.messageContainer;
        const mm = ro.modelMessage;
        const mesh2d = params.mesh2d;
        const id = MessengerLayoutHelper._nextControlId;
        const rootContainer = new Container(`modelMessageContainer${id}`);
        rootContainer.adaptHeightToChildren = true;
        const avatarCircle = new Rectangle3D(`modelMessageAvatarCircle${id}`);
        avatarCircle.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        avatarCircle.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
        avatarCircle.widthInPixels = mesh2d.m2px(mm.avatar.width);
        avatarCircle.heightInPixels = mesh2d.m2px(mm.avatar.height);
        avatarCircle.leftInPixels = mesh2d.m2px(mm.avatar.left);
        avatarCircle.topInPixels = mesh2d.m2px(mm.avatar.top);
        avatarCircle.useArc = true;
        avatarCircle.cornerRadius = Math.round(mesh2d.m2px(mm.avatar.cornerRadius));
        rootContainer.addControl(avatarCircle);
        if (params.avatarImage) {
            avatarCircle.addControl(params.avatarImage);
        }
        const bubble = new Rectangle3D(`modelMessageBubble${id}`);
        bubble.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        bubble.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
        bubble.widthInPixels = mesh2d.m2px(mm.bubble.width);
        bubble.adaptHeightToChildren = true;
        bubble.leftInPixels = mesh2d.m2px(mm.bubble.left);
        bubble.cornerRadius = Math.round(mesh2d.m2px(mm.bubble.cornerRadius));
        bubble.background = mm.bubble.background;
        rootContainer.addControl(bubble);
        const richText = parseTextWithLinksToColoredRichText(params.text, mm.linkColor);
        const hasLinks = richText !== params.text;
        const messageTextBlock = hasLinks
            ? ObjectBuilder.createRichTextBlock3D(`modelMessageText${id}`, richText, mm.messageText, RichTextGrammar.RTTag, mesh2d)
            : ObjectBuilder.createTextBlock3D(mesh2d, `modelMessageText${id}`, params.text, mm.messageText);
        messageTextBlock.enableSymbolWrap = true;
        if (hasLinks) {
            messageTextBlock.isHitTestVisible = true;
            messageTextBlock.onHyperlinkClickedObservable.add(params.linkClickHandler);
        }
        bubble.addControl(messageTextBlock);
        const timeText = MessengerLayoutHelper._getTimeText(params.date);
        const timeTextBlock = ObjectBuilder.createTextBlock3D(mesh2d, `modelMessageTime${id}`, timeText, mm.timeText);
        bubble.addControl(timeTextBlock);
        return {
            rootContainer,
            dateTextBlock: null,
            newMessagesTextBlock: null,
            statusImages: null,
        };
    }
    static createUserTips(params) {
        const ro = Env.common.messengerPanel.chat.messageContainer;
        const ut = ro.userTips;
        const mesh2d = params.mesh2d;
        const id = MessengerLayoutHelper._nextControlId;
        const rootContainer = new Container(`userTipsContainer${id}`);
        rootContainer.adaptHeightToChildren = true;
        const bubble = new Rectangle3D(`userTipsBubble${id}`);
        bubble.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_RIGHT;
        bubble.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
        bubble.widthInPixels = mesh2d.m2px(ut.bubble.width);
        bubble.adaptHeightToChildren = true;
        bubble.leftInPixels = mesh2d.m2px(ut.bubble.left);
        bubble.cornerRadius = Math.round(mesh2d.m2px(ut.bubble.cornerRadius));
        bubble.thickness = Math.round(mesh2d.m2px(ut.bubble.borderThickness));
        bubble.color = ut.bubble.borderColor;
        rootContainer.addControl(bubble);
        const currencyImage = new Image3D(`userTipsCurrencyImage${id}`, ut.currency.src);
        currencyImage.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        currencyImage.verticalAlignment = Container.VERTICAL_ALIGNMENT_CENTER;
        currencyImage.widthInPixels = mesh2d.m2px(ut.currency.width);
        currencyImage.heightInPixels = mesh2d.m2px(ut.currency.height);
        currencyImage.leftInPixels = mesh2d.m2px(ut.currency.left);
        bubble.addControl(currencyImage);
        const tipsText = StringUtils.stringFormat(text.common_messenger_chatlist_tips, params.tipsAmount);
        const messageTextBlock = ObjectBuilder.createTextBlock3D(mesh2d, `userTipsText${id}`, tipsText, ut.tipsText);
        bubble.addControl(messageTextBlock);
        const timeText = MessengerLayoutHelper._getTimeText(params.date);
        const timeTextBlock = ObjectBuilder.createTextBlock3D(mesh2d, `userMessageTime${id}`, timeText, ut.timeText);
        bubble.addControl(timeTextBlock);
        return {
            rootContainer,
            dateTextBlock: null,
            newMessagesTextBlock: null,
            statusImages: null,
        };
    }
    static createUserMessage(params) {
        const ro = Env.common.messengerPanel.chat.messageContainer;
        const um = ro.userMessage;
        const mesh2d = params.mesh2d;
        const id = MessengerLayoutHelper._nextControlId;
        const rootContainer = new Container(`userMessageContainer${id}`);
        rootContainer.adaptHeightToChildren = true;
        const bubble = new Rectangle3D(`userMessageBubble${id}`);
        bubble.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_RIGHT;
        bubble.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
        bubble.widthInPixels = mesh2d.m2px(um.bubble.width);
        bubble.adaptHeightToChildren = true;
        bubble.leftInPixels = mesh2d.m2px(um.bubble.left);
        bubble.cornerRadius = Math.round(mesh2d.m2px(um.bubble.cornerRadius));
        bubble.background = um.bubble.background;
        rootContainer.addControl(bubble);
        const richText = parseTextWithLinksToColoredRichText(params.text, um.linkColor);
        const hasLinks = richText !== params.text;
        const messageTextBlock = hasLinks
            ? ObjectBuilder.createRichTextBlock3D(`userMessageText${id}`, richText, um.messageText, RichTextGrammar.RTTag, mesh2d)
            : ObjectBuilder.createTextBlock3D(mesh2d, `userMessageText${id}`, params.text, um.messageText);
        messageTextBlock.enableSymbolWrap = true;
        if (hasLinks) {
            messageTextBlock.isHitTestVisible = true;
            messageTextBlock.onHyperlinkClickedObservable.add(params.linkClickHandler);
        }
        bubble.addControl(messageTextBlock);
        const timeText = MessengerLayoutHelper._getTimeText(params.date);
        const timeTextBlock = ObjectBuilder.createTextBlock3D(mesh2d, `userMessageTime${id}`, timeText, um.timeText);
        bubble.addControl(timeTextBlock);
        const createStatusImage = (url) => {
            const result = new Image3D(`userMessageStatusSent${id}`, url);
            result.widthInPixels = mesh2d.m2px(um.messageStatus.width);
            result.heightInPixels = mesh2d.m2px(um.messageStatus.height);
            result.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
            result.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
            result.leftInPixels = mesh2d.m2px(um.messageStatus.left);
            result.topInPixels = mesh2d.m2px(um.messageStatus.top);
            return result;
        };
        const sentImage = createStatusImage(um.messageStatus.imgSent);
        bubble.addControl(sentImage);
        const readImage = createStatusImage(um.messageStatus.imgRead);
        bubble.addControl(readImage);
        sentImage.isVisible = params.status === "SENT";
        readImage.isVisible = params.status === "READ";
        return {
            rootContainer,
            dateTextBlock: null,
            newMessagesTextBlock: null,
            statusImages: {
                sent: sentImage,
                read: readImage,
            },
        };
    }
    static _getDateText(date) {
        return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    }
    static _getTimeText(date) {
        return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    }
}
MessengerLayoutHelper._currentControlId = 0;
